import type { TablePaginationConfig } from 'ant-design-vue'
import { getApiBase } from '../composables/useApiBase'
export const defaultSorts = [{ field: 'id', dir: 'desc' }]
export enum Sort {
  Asc = 'Asc',
  Desc = 'Desc',
}

export interface SortParam {
  field: string
  dir: string
}

export interface FetchIncludeObject {
  include: FetchInclude
}

export interface FetchInclude {
  [key: string]: boolean | FetchIncludeObject
}

export interface FetchListParam<T> {
  page: number
  limit: number
  sorts: SortParam[]
  filters?: Record<string, any>
  include?: FetchInclude
}

export interface ListFetchEvent {
  fetchParam: FetchListParam<any>
  pagination: TablePaginationConfig
  filters?: Record<string, any>
  result: FetchListResult
}

export interface FetchListResult {
  list: any[]
  page: number
  limit: number
  count: number
}

export async function dataFetchList<T>(
  model:  {
    table: string
  } ,
  params: FetchListParam<T>,
  { authentication = true }: { authentication?: boolean } = {},
): Promise<FetchListResult> {
  const v2ApiBase = getApiBase()
  if (!model?.table) {
    console.error('Invalid model', model)
    throw new Error('Invalid model')
  }
  const tableName = (() => {
    // if (model.table.endsWith('s') && model.table !== 'category') {
    //   return model.table.substring(0, model.table.length - 1)
    // }
    return model.table
  })()
  const $acl = useAclStore()
  // console.log('v2apiBase', v2ApiBase)
  if (params.filters?.id === 'installHook.js.map') {
    return {
      list: [],
      page: 1,
      limit: 1,
      count: 0,
    }
  }
  const response = await fetch(`${v2ApiBase}/api/v2/crud/fetch/${tableName}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(authentication === false
        ? {}
        : { Authorization: `Bearer ${$acl.currentUser?.accessToken}` }),
    },
    body: JSON.stringify({
      page: params.page,
      limit: params.limit,
      sorts: params.sorts,
      filters: $acl.currentUser?.profile?.roles?.includes('superadmin')
        ? params.filters
        : {
            ...params.filters,
          },
      include: params.include,
    }),
  })

  if (!response.ok) {
    console.error(
      'fetch error',
      { tableName, params, response: response.body },
      JSON.stringify(params, null, 2),
    )
    const errorStack = await response.text()
    console.error('Error details:', errorStack)

    throw new Error(`HTTP error! status: ${response.status}`)
  }

  return await response.json()
}

/**
 * Caches the result of a function call with a specified TTL (Time To Live)
 * @param fn The function to cache results for
 * @param options Cache configuration options
 * @param options.cacheKey Function that generates a cache key from the arguments
 * @param options.ttl Time to live in milliseconds (default: 5 minutes)
 * @param args Arguments to pass to the function
 * @returns A promise that resolves to the function's return value
 */
export async function cacheFunction<T extends (...args: any[]) => any>(
  fn: T,
  options: {
    cacheKey: (...args: Parameters<T>) => string | null
    ttl?: number
  },
  ...args: Parameters<T>
): Promise<ReturnType<T>> {
  if (typeof window === 'undefined') {
    return fn(...args)
  }
  const { cache } = await import('./cache.server')
  const key = options.cacheKey(...args)
  if (key === null) {
    return Promise.resolve(fn(...args))
  }

  const cachedResult = cache.get(key)

  if (cachedResult !== undefined) {
    console.log('cache hit', key)
    return Promise.resolve(cachedResult as ReturnType<T>)
  }

  const result = fn(...args)
  console.log('cacheFunction set', key, result)
  cache.set(key, result, { ttl: options.ttl ?? 1000 * 60 * 5 })

  return Promise.resolve(result)
}

export async function invalidateCache(modelName: string): Promise<void> {
  const { cache } = await import('./cache.server')
  const keysToInvalidate: string[] = []
  console.log('cache?', cache)
  for (const [key] of Array.from(cache.entries()) as [string, unknown][]) {
    if (key.startsWith(modelName)) {
      keysToInvalidate.push(key)
    }
  }

  for (const key of keysToInvalidate) {
    cache.delete(key)
  }
  console.log(
    `Invalidated ${keysToInvalidate.length} cache entries for model: ${modelName}`,
  )
}

export const cachableDataFetchList = async (
  model: { table: string },
  params: FetchListParam<any>,
  options?: { authentication?: boolean },
) =>
  cacheFunction(
    dataFetchList,
    {
      cacheKey: (model, p) =>
        ['Category', 'Product'].includes(model.table)
          ? `${model.table}-${JSON.stringify({ p })}`
          : null,
      ttl: 5 * 60 * 1000, // 5 minutes
    },
    model,
    params,
    options || {},
  )
